import axios from 'axios';
import { toast } from 'sonner';

// Global axios interceptor — adds JWT token to every request
axios.interceptors.request.use(config => {
  const token = localStorage.getItem('fec_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Throttle still-loading toast: many endpoints fan-out in parallel and any
// one timing out shouldn't flood the user with duplicate banners. Show at
// most one "still loading" toast every 6 seconds.
let _lastStillLoadingToast = 0;
// Flag set when a still-loading toast fires; consumed by the first
// subsequent successful response so we can confirm "data ready" once.
let _pendingDataReady = false;

function _maybeShowStillLoading(message) {
  const now = Date.now();
  if (now - _lastStillLoadingToast < 6000) return;
  _lastStillLoadingToast = now;
  _pendingDataReady = true;
  toast.info(message, {
    id: 'still-loading',
    duration: 4000,
    description: 'Loading large dataset — please retry in a moment.',
  });
}

function _maybeShowDataReady() {
  if (!_pendingDataReady) return;
  _pendingDataReady = false;
  toast.success('Data ready', {
    id: 'data-ready',
    duration: 2000,
    description: 'Refreshing your view…',
    className: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  });
}

// Expose helpers so other modules (e.g. useMapData using raw fetch) can
// participate in the same toast lifecycle.
export const _stillLoadingToast = _maybeShowStillLoading;
export const _dataReadyToast = _maybeShowDataReady;

// Response interceptor — surface 503 still_loading as a friendly toast and
// fire a "data ready" confirmation on the first successful response after
// one. Existing retry behaviour in calling code is preserved.
axios.interceptors.response.use(
  (response) => {
    _maybeShowDataReady();
    return response;
  },
  (error) => {
    try {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 503 && data && data.error === 'still_loading') {
        _maybeShowStillLoading(data.message || 'Still loading — please try again in a moment.');
      }
    } catch (_) {
      // never let the interceptor itself throw
    }
    return Promise.reject(error);
  },
);

// For raw fetch() calls
export function authHeaders() {
  const token = localStorage.getItem('fec_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Authenticated fetch wrapper. Also detects 503 still_loading bodies for
// non-axios callers (e.g. the map data hook uses fetch directly).
export async function authFetch(url, options = {}) {
  const headers = { ...authHeaders(), ...options.headers };
  const res = await fetch(url, { ...options, headers });
  if (res.status === 503) {
    try {
      const cloned = res.clone();
      const body = await cloned.json();
      if (body && body.error === 'still_loading') {
        _maybeShowStillLoading(body.message || 'Still loading — please try again in a moment.');
      }
    } catch (_) { /* non-JSON 503, ignore */ }
  } else if (res.ok) {
    _maybeShowDataReady();
  }
  return res;
}
